import { encounterCreatureField } from '../../shared/array-types.js';
import { storedStatusRows } from './creature-status-reader.js';
import { readItemField } from './item-field-reader.js';
import { readLayerListEntryUuid } from './inventory-raw-readers.js';
import { DEFENSE_SLOTS, STATUS_DAMAGE_DEFENSE_LAYERS } from './creature-defense-delta.js';
import { foldCreatureDefenseEntries, normalizeDefenseSource } from './creature-defense.js';
import { statusItemId } from '../statuses/expiry.js';

type Row = Record<string, unknown>;
const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
export const STATUS_DEFENSE_LAYERS = new Set<string>([...STATUS_DAMAGE_DEFENSE_LAYERS, 'immune_status_effect']);
interface Readers {
  definition: (id: string) => Row | null;
  entries: (row: Row, key: string) => unknown[];
}

/** Status layers seed host-attributed defenses before permanent/feat/passive
 * providers. Remove only that root share; a surviving provider takes its place
 * through the same first-wins fold used by native construction. */
export function projectStatusDefenseRemoval(row: Row, statusIds: readonly string[], read: Readers):
  { ok: true; defenses: Row } | { ok: false; reason: string } {
  const removed = new Set(statusIds.map(upper));
  const statuses = storedStatusRows(row, 'target');
  const definition = (status: Row): Row | null => record(status.status_arr)
    ?? read.definition(statusItemId(status));
  const lost = statuses.filter(status => removed.has(upper(readItemField(status, 'status_id')))).map(definition);
  const kept = statuses.filter(status => !removed.has(upper(readItemField(status, 'status_id')))).map(definition);
  if ([...lost, ...kept].some(def => !def)) return { ok: false, reason: 'definition-missing' };
  const creature = record(record(row.computed)?.creature);
  const defenses = record(creature?.defenses) ?? {};
  let result = defenses;
  const hostId = upper(encounterCreatureField(row.encounter_creatures, 'item_id') ?? readItemField(row, 'item_id'));
  const host = encounterCreatureField(row.encounter_creatures, 'item_type') === 'player_character' ? null : read.definition(hostId);
  if (!host && encounterCreatureField(row.encounter_creatures, 'item_type') !== 'player_character')
    return { ok: false, reason: 'host-definition-missing:' + hostId };
  for (const layer of STATUS_DEFENSE_LAYERS) {
    const spec = DEFENSE_SLOTS[layer]!, family = spec.key === 'damage_type' ? 'damage' : 'status';
    const idOf = (entry: unknown): string => upper(readItemField(record(entry), spec.key) ?? readLayerListEntryUuid(entry));
    const ids = (def: Row): string[] => read.entries(def, layer).map(idOf).filter(Boolean);
    const affected = new Set(lost.flatMap(def => ids(def!)));
    if (!affected.size) continue;
    const retained = new Set([...(host ? ids(host) : []), ...kept.flatMap(def => ids(def!))]);
    const initial = Array.isArray(defenses[spec.slot]) ? defenses[spec.slot] as unknown[] : [];
    const next = initial.flatMap(entry => {
      const source = record(entry), id = idOf(entry);
      if (!source || !affected.has(id) || retained.has(id)) return [entry];
      const contributors = [source, ...(Array.isArray(source.all_sources) ? source.all_sources : [])].filter(record);
      const remaining = contributors.filter(meta => upper(meta.source_item_id) !== hostId);
      if (remaining.length === contributors.length) return [entry];
      return foldCreatureDefenseEntries({ family, initialEntries: [], events: remaining.map(meta =>
        ({ uuid: String(source[spec.key]), source: normalizeDefenseSource(meta, family) })) });
    });
    if (next.length === initial.length && next.every((entry, index) => entry === initial[index])) continue;
    if (result === defenses) result = { ...defenses };
    if (next.length) result[spec.slot] = next; else delete result[spec.slot];
  }
  return { ok: true, defenses: result };
}
